import React, { useState, useContext, useMemo } from "react";
import { useMutation } from "react-apollo";

import {
  Modal,
  Stack,
  Select,
  TextField,
  Checkbox,
  Popover,
  OptionList,
  Button,
  List,
  DisplayText,
} from "@shopify/polaris";

import Banner from "lib/components/banner/banner";
import AutoComplete from "lib/components/autocomplete/autocomplete";
import constant from "lib/constant/constant";
import baseHelper from "lib/helpers/base";

import { PrivateContext } from "lib/context";
import { errorHelper } from "lib/helpers";
import { ADD_RULE } from "app/shipping/apollo/mutations";
import { addProp } from "app/shipping/modules/provider/features/subFeatures/advance/rules/props";

import { AdvanceShippingContext } from "app/shipping/modules/provider/features/subFeatures/advance/context/context";

const AddNewRule = (props) => {
  const { isVendorAllowed, setBannerOuter } = useContext(AdvanceShippingContext);
  const { cms } = useContext(PrivateContext);
  const { addNewRuleModal, setAddNewRuleModal, destinations, band, products, refetch, moneyFormat } = props;
  const [addRule, { loading: addRuleLoading }] = useMutation(ADD_RULE);
  const { value: cValue, gql } = constant;

  const [destination, setDestination] = useState({});
  const [isShippingDisabled, setIsShippingDisabled] = useState(false);
  const [shippingBandId, setShippingBandId] = useState("");
  const [condition, setCondition] = useState("");
  const [priceType, setPriceType] = useState("");
  const [price, setPrice] = useState(0);
  const [popoverActive, setPopoverActive] = useState(false);
  const [statePopoverActive, setStatePopoverActive] = useState(null);
  const [regionSearch, setRegionSearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [productIds, setProductIds] = useState([]);
  const [description, setDescription] = useState("");
  const [ruleBanner, setRuleBanner] = useState({
    isOpen: false,
    title: "",
    message: "",
    status: "",
  });

  const normalizedDestinations = useMemo(() => {
    if (!destinations || !Array.isArray(destinations)) return [];
    return destinations;
  }, [destinations]);

  const filteredDestinations = useMemo(() => {
    if (!regionSearch) return normalizedDestinations;
    const search = regionSearch.toLowerCase().trim();
    return normalizedDestinations
      .map((section) => {
        const options =
          (section.options || []).filter((opt) => {
            return (
              (opt.label && opt.label.toLowerCase().includes(search)) ||
              (opt.value && String(opt.value).toLowerCase().includes(search)) ||
              (section.title && section.title.toLowerCase().includes(search))
            );
          }) || [];
        return { ...section, options };
      })
      .filter((section) => section.options && section.options.length);
  }, [normalizedDestinations, regionSearch]);

  const option = {
    priceTypeOptions: [
      { label: cms("label.fixed"), value: constant.FIXED },
      { label: cms("modal.rule.add.label.free"), value: constant.FREE },
    ],
    conditionOptions: [
      { label: cms("modal.rule.add.label.destination"), value: constant.DESTINATION },
      { label: cms("modal.rule.add.label.product"), value: constant.PRODUCT },
    ],
  };

  const toggleBanner = () => setRuleBanner({ isOpen: false });
  const handleChange = () => setAddNewRuleModal(!addNewRuleModal);
  const togglePopoverActive = () => {
    if (popoverActive) {
      setStatePopoverActive(null);
      setRegionSearch("");
      setStateSearch("");
    }
    setPopoverActive(!popoverActive);
  };
  const togglePopover = () => setPopoverActive(!popoverActive);

  const acceptOnlyValidInput = (value, prevValue) => {
    return (baseHelper.validatePositiveNumericValues(value) && value) || (value !== "" && prevValue) || "";
  };

  const handleShippingDestination = (value) => {
    const newDestination = {};
    (value || []).forEach((countryCode) => {
      newDestination[countryCode] = (destination || {})[countryCode] ?? [];
    });
    setDestination(newDestination);
  };
  const handleDescriptionChange = (value) => setDescription(value);
  const handleProductChange = (value) => setProductIds(value);
  const handleSelectPriceType = (value) => setPriceType(value);
  const handleSelectShippingRule = (region) => setCondition(region);
  const handleShippingBand = (value) => setShippingBandId(value);
  const handleFixedValue = (value) => setPrice(acceptOnlyValidInput(value, price));

  const handleStateSelection = (countryCode, stateValues) => {
    setDestination((prev) => ({
      ...prev,
      [countryCode]: stateValues || [],
    }));
  };

  const getSelectedCountryWithStates = () => {
    const result = [];
    normalizedDestinations.forEach((region) => {
      (region.options || []).forEach((country) => {
        if (destination.hasOwnProperty(country.value)) {
          result.push({
            ...country,
            states: country.states || [],
          });
        }
      });
    });
    return result;
  };

  const renderDestinations = () => {
    const selectedCountries = getSelectedCountryWithStates();
    if (!selectedCountries.length) return null;

    return (
      <Stack vertical>
        <Stack.Item>
          <DisplayText size={constant.SMALL}>{cms("modal.rule.add.label.selected")}</DisplayText>
        </Stack.Item>
        {selectedCountries.map((country) => {
          const selectedStateCodes = destination[country.value] || [];
          const filteredStates =
            (country.states || []).filter((s) => {
              if (!stateSearch) return true;
              const search = stateSearch.toLowerCase().trim();
              return (
                (s.label && s.label.toLowerCase().includes(search)) ||
                (s.value && String(s.value).toLowerCase().includes(search))
              );
            }) || [];
          return (
            <Stack.Item key={country.value}>
              <Stack vertical>
                <Stack.Item>
                  <Stack>
                    <Stack.Item fill>
                      <DisplayText size={constant.SMALL}>
                        <strong>{country.label}</strong>
                      </DisplayText>
                    </Stack.Item>
                    {country.states && country.states.length > 0 && (
                      <Stack.Item>
                        <Popover
                          active={statePopoverActive === country.value}
                          activator={
                            <Button
                              size="slim"
                              onClick={() => {
                                const isSame = statePopoverActive === country.value;
                                setStatePopoverActive(isSame ? null : country.value);
                                if (!isSame) {
                                  setStateSearch("");
                                }
                              }}
                            >
                              {cms("modal.rule.add.button.selectStates") || "Select states"}
                            </Button>
                          }
                          onClose={() => {
                            setStatePopoverActive(null);
                            setStateSearch("");
                          }}
                        >
                          <div style={{ padding: "12px", minWidth: "260px", maxHeight: "300px", overflowY: "auto" }}>
                            <Stack vertical spacing="tight">
                              <Stack.Item>
                                <TextField
                                  value={stateSearch}
                                  onChange={setStateSearch}
                                  placeholder={
                                    cms("modal.rule.add.placeholder.searchState") || "Search state or province"
                                  }
                                />
                              </Stack.Item>
                              <Stack.Item>
                                <OptionList
                                  onChange={(val) => handleStateSelection(country.value, val)}
                                  sections={[
                                    {
                                      options: filteredStates.map((s) => ({
                                        value: s.value,
                                        label: s.label,
                                      })),
                                    },
                                  ]}
                                  selected={selectedStateCodes}
                                  allowMultiple
                                />
                              </Stack.Item>
                            </Stack>
                          </div>
                        </Popover>
                      </Stack.Item>
                    )}
                  </Stack>
                </Stack.Item>
                {selectedStateCodes.length > 0 ? (
                  <Stack.Item>
                    <List type={constant.BULLET}>
                      {selectedStateCodes.map((stateCode) => {
                        const state = (country.states || []).find((s) => s.value === stateCode);
                        return state ? <List.Item key={stateCode}>{state.label}</List.Item> : null;
                      })}
                    </List>
                  </Stack.Item>
                ) : (
                  country.states?.length > 0 && (
                    <Stack.Item>
                      <DisplayText size={constant.SMALL} tone="subdued">
                        All states
                      </DisplayText>
                    </Stack.Item>
                  )
                )}
              </Stack>
            </Stack.Item>
          );
        })}
      </Stack>
    );
  };

  const addShippingBand = () => {
    const data = {
      condition,
      description,
      destination: destination || {},
      isShippingDisabled,
      price: priceType === constant.FIXED ? parseFloat(price) || 0 : 0,
      priceType,
      productIds,
      shippingBandId,
    };
    addRule({ variables: { input: data } })
      .then((response) => {
        const responseError = baseHelper.getResponseError(response.data, gql.CREATE_SHIPPING_RULE);
        if (responseError) {
          setRuleBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
          return;
        }
        refetch();
        setBannerOuter({
          isOpen: true,
          title: cms("modal.rule.add.message.success.successfullyAdded"),
          status: "success",
        });
        setDestination({});
        setShippingBandId("");
        setCondition("");
        setPriceType("");
        setPrice(0);
        setProductIds([]);
        setDescription("");
        setIsShippingDisabled(false);
        setStatePopoverActive(null);
        setPopoverActive(false);
        setAddNewRuleModal(false);
      })
      .catch((exception) => {
        setRuleBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
      });
  };

  const checkValidation = () => {
    if (!shippingBandId) return cms("message.error.selectShippingBand");
    if (!condition) return cms("message.error.selectShippingCondition");
    if (condition === constant.DESTINATION && !Object.keys(destination).length) {
      return cms("message.error.selectAtleastOneDestination");
    }
    if (condition === constant.PRODUCT && !(productIds && productIds.length)) {
      return cms("message.error.selectAtleastOneProduct");
    }
    if (!description) return cms("message.error.addDescriptionForRule");
    if (description.length > 255) return cms("message.error.descriptionCharacter");
    if (!priceType) return cms("message.error.selectShippingRuleType");
    if (priceType !== constant.FREE && !price) return cms("message.error.provideCost");
    if (priceType !== constant.FREE && parseFloat(price) < 0) {
      return cms("message.error.provideValidPrice");
    }
    return false;
  };

  const handleSubmit = () => {
    const hasError = checkValidation();
    if (hasError) {
      setRuleBanner({ isOpen: true, title: hasError, status: constant.CRITICAL });
      return false;
    }
    addShippingBand();
    return true;
  };

  if (!isVendorAllowed) return null;

  const isDestination = condition === constant.DESTINATION;
  const isProduct = condition === constant.PRODUCT;
  const activator = (
    <Button onClick={togglePopoverActive} disclosure>
      {cms("modal.rule.add.button.selectRegion")}
    </Button>
  );

  return (
    <>
      <Modal
        open={addNewRuleModal}
        onClose={handleChange}
        title={cms("modal.rule.add.title")}
        primaryAction={{
          content: cms("modal.rule.add.button.primary"),
          loading: addRuleLoading,
          onAction: handleSubmit,
        }}
      >
        <Modal.Section>
          <Stack vertical>
            <Stack.Item>
              {ruleBanner.isOpen && (
                <Banner
                  isOpen={ruleBanner.isOpen}
                  title={ruleBanner.title}
                  message={ruleBanner.message}
                  status={ruleBanner.status}
                  onDismiss={toggleBanner}
                />
              )}
            </Stack.Item>
            <Stack.Item>
              <Select
                label={cms("modal.rule.add.label.selectShippingBand")}
                placeholder={cms("modal.rule.add.label.selectShippingBand")}
                options={band}
                onChange={handleShippingBand}
                value={shippingBandId}
              />
            </Stack.Item>
            <Stack.Item>
              <Select
                label={cms("modal.rule.add.label.selectShippingRule")}
                options={option.conditionOptions}
                onChange={handleSelectShippingRule}
                value={condition}
                placeholder={cms("modal.rule.add.label.selectShippingRule")}
              />
            </Stack.Item>
            {isProduct && (
              <Stack.Item>
                <AutoComplete
                  label=""
                  key={Array.isArray(products) ? products.length : "products"}
                  values={products || []}
                  onChange={handleProductChange}
                  selected={productIds}
                  placeholder={cms("modal.rule.add.placeholder.startTyping")}
                  minimumSearchLength={Number(3)}
                />
              </Stack.Item>
            )}
            {isDestination && (
              <>
                <Stack.Item>
                  <Popover active={popoverActive} activator={activator} onClose={togglePopover}>
                    <div style={{ padding: "12px", minWidth: "320px", maxHeight: "400px", overflowY: "auto" }}>
                      <Stack vertical spacing="tight">
                        <Stack.Item>
                          <TextField
                            value={regionSearch}
                            onChange={setRegionSearch}
                            placeholder={
                              cms("modal.rule.add.placeholder.searchRegion") || "Search region or country"
                            }
                          />
                        </Stack.Item>
                        <Stack.Item>
                          <OptionList
                            onChange={handleShippingDestination}
                            sections={filteredDestinations}
                            selected={Object.keys(destination)}
                            allowMultiple
                          />
                        </Stack.Item>
                      </Stack>
                    </div>
                  </Popover>
                </Stack.Item>
                <Stack.Item>{renderDestinations()}</Stack.Item>
              </>
            )}
            <Stack.Item>
              <TextField
                label={`${cms("modal.rule.add.label.description")}*`}
                value={description}
                onChange={handleDescriptionChange}
                placeholder={cms("modal.rule.add.placeholder.description")}
              />
            </Stack.Item>
            <Stack.Item>
              <Stack>
                <Stack.Item>
                  <Select
                    label={cms("modal.rule.add.label.selectShippingType")}
                    options={option.priceTypeOptions}
                    onChange={handleSelectPriceType}
                    value={priceType || ""}
                    placeholder={cms("modal.rule.add.placeholder.selectShipping")}
                  />
                </Stack.Item>
                {priceType === constant.FIXED && (
                  <Stack.Item>
                    <TextField
                      prefix={moneyFormat}
                      label={cms("modal.rule.add.label.amount")}
                      onChange={handleFixedValue}
                      value={(price || 0).toString()}
                      min={cValue.MIN_PRICE}
                      step={cValue.STEP}
                    />
                  </Stack.Item>
                )}
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <Checkbox
                label={cms("modal.rule.add.label.isAvailableForShipping")}
                checked={isShippingDisabled}
                onChange={() => setIsShippingDisabled(!isShippingDisabled)}
              />
            </Stack.Item>
          </Stack>
        </Modal.Section>
      </Modal>
    </>
  );
};
AddNewRule.propTypes = addProp.type;
export default AddNewRule;
